/**
 * Pipeline Dashboard Configuration
 * Columns and action definitions
 */
import type { TFunction } from 'i18next'
import type {
    DataTableColumn,
    DataTableAction,
} from '@/components/ui/data-table-customized'
import {
    createChipFormatter,
    createDateFormatter,
} from '@/components/ui/data-table-customized'
import { RefreshCw } from 'lucide-react'
import type { TaskWithCustomer } from '../types/response'

export const getPipelineColumns = (
    t: TFunction
): DataTableColumn<TaskWithCustomer>[] => [
    {
        key: 'id',
        title: t('pipeline.columns.task_id'),
        width: 70,
        render: (value) => `#${value}`,
    },
    {
        key: 'customer_name',
        title: t('fields.customer_name'),
        minWidth: 180,
        flex: 1,
    },
    {
        key: 'report_type',
        title: t('fields.frequency'),
        width: 100,
    },
    {
        key: 'send_status',
        title: t('fields.status'),
        width: 120,
        formatter: createChipFormatter({
            colorMap: {
                pending: { color: 'warning' },
                running: { color: 'info' },
                completed: { color: 'success' },
                failed: { color: 'error' },
                cancelled: { color: 'default' },
            },
        }),
    },
    {
        key: 'created_at',
        title: t('fields.created_at'),
        width: 150,
        formatter: createDateFormatter({ format: 'DD/MM/YYYY HH:mm' }),
    },
    {
        key: 'error_message',
        title: t('pipeline.columns.error'),
        minWidth: 200,
        flex: 1,
        render: (value, row) => {
            if (!value) return '-'
            return (
                <div
                    className="truncate text-xs text-destructive"
                    title={value as string}
                >
                    {row.error_step && (
                        <span className="font-medium mr-1">
                            [
                            {t(`pipeline.error_steps.${row.error_step}`, {
                                defaultValue: row.error_step,
                            })}
                            ]
                        </span>
                    )}
                    {value as string}
                </div>
            )
        },
    },
]

export const getPipelineActions = (
    t: TFunction,
    handlers: {
        onRetry: (row: TaskWithCustomer) => void
    }
): DataTableAction<TaskWithCustomer>[] => [
    {
        key: 'retry',
        label: t('pipeline.retry'),
        icon: <RefreshCw className="w-4 h-4" />,
        onClick: handlers.onRetry,
        hidden: (row) => row.send_status !== 'failed',
    },
]
